import { useEffect, useState } from "react";

import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { useReloadToken } from "@/modules/candidate/hooks/useReloadToken.hook";

// Fetches the locales a candidate may pick for an invite (the assessment's enabled locales).
// The intro page gates on this fetch: it must resolve before we know whether a language choice is
// required, so loading and error are surfaced (rather than silently degrading) and the caller can
// hold the candidate on a loading/retry state instead of letting them slip past the picker.
export function useInviteAvailableLanguagesHook(inviteId?: number) {
  const [availableLocales, setAvailableLocales] = useState<LanguagesIsoEnum[]>([]);
  // Start in the loading state when we already have an invite so the intro page doesn't flash before
  // the fetch effect runs.
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(inviteId));
  const [error, setError] = useState<boolean>(false);
  const { reloadToken, retry } = useReloadToken();

  useEffect(() => {
    if (!inviteId) {
      setAvailableLocales([]);
      setIsLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;

    const fetchLanguages = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const { languages } = await InvitesHttpService.getInviteAvailableLanguages(inviteId);
        if (!cancelled) setAvailableLocales(languages);
      } catch {
        if (!cancelled) {
          setAvailableLocales([]);
          setError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchLanguages();

    return () => {
      cancelled = true;
    };
  }, [inviteId, reloadToken]);

  return { availableLocales, isLoading, error, retry };
}
